import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { toast } from 'sonner';
import { organizationKeys } from '@/hooks/queries/useOrganizationsQuery';

export const organizationUserKeys = {
  all: ['organizationUsers'] as const,
  lists: () => [...organizationUserKeys.all, 'list'] as const,
  list: (organizationId: number) => [...organizationUserKeys.lists(), organizationId] as const,
};

export const pendingInvitationKeys = {
  all: ['pendingOrganizationInvitations'] as const,
  list: () => [...pendingInvitationKeys.all, 'list'] as const,
};

export const useOrganizationUsersQuery = (organizationId: number) => {
  return useQuery({
    queryKey: organizationUserKeys.list(organizationId),
    queryFn: () => apiClient.getOrganizationUsers(organizationId),
    enabled: !!organizationId,
    gcTime: 5 * 60 * 1000,
  });
};

export const useInviteUserToOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, email }: { organizationId: number; email: string }) =>
      apiClient.inviteUserToOrganization(organizationId, email),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: organizationUserKeys.list(organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(organizationId) });
      toast.success('Invitation sent');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to send invitation');
    },
  });
};

export const useUpdateOrganizationUserRoleMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, userId, roleId }: { organizationId: number; userId: number; roleId: number }) =>
      apiClient.updateOrganizationUserRole(organizationId, userId, roleId),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: organizationUserKeys.list(organizationId) });
      toast.success('Role updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update role');
    },
  });
};

export const useRemoveOrganizationUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, userId }: { organizationId: number; userId: number }) =>
      apiClient.removeOrganizationUser(organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: organizationUserKeys.list(organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(organizationId) });
      toast.success('User removed from organization');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to remove user');
    },
  });
};

export const usePendingOrganizationInvitationsQuery = () => {
  return useQuery({
    queryKey: pendingInvitationKeys.list(),
    queryFn: () => apiClient.getPendingOrganizationInvitations(),
    gcTime: 5 * 60 * 1000,
  });
};

export const useAcceptOrganizationInvitationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId }: { organizationId: number }) =>
      apiClient.acceptOrganizationInvitation(organizationId),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: pendingInvitationKeys.list() });
      queryClient.invalidateQueries({ queryKey: organizationUserKeys.list(organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      toast.success('Invitation accepted');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to accept invitation');
    },
  });
};

export const useDeclineOrganizationInvitationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId }: { organizationId: number }) =>
      apiClient.declineOrganizationInvitation(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingInvitationKeys.list() });
      toast.success('Invitation declined');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to decline invitation');
    },
  });
};
